<template>
  <div class="wiki-page">
    <!-- Modern Header -->
    <div class="wiki-header">
      <div class="header-left">
        <h1 class="page-title">Wiki</h1>
        <p class="page-subtitle">Your team's knowledge base &mdash; templates, guides, and reference docs</p>
      </div>
      <div class="header-actions">
        <b-button variant="outline-primary" class="import-btn mr-2" @click="openImportModal">
          <i class="fa fa-upload" aria-hidden="true"></i> Import
        </b-button>
        <b-button variant="success" class="create-btn" @click="gotoCreatePage()">
          <i class="fa fa-plus" aria-hidden="true"></i> New Wiki Page
        </b-button>
      </div>
    </div>

    <!-- Stats Overview -->
    <div v-if="wikiPages && wikiPages[0] && wikiPages[0].length > 0" v-show="!show" class="stats-bar">
      <div class="stat-card">
        <div class="stat-icon stat-icon-blue">
          <i class="fa fa-file-text-o"></i>
        </div>
        <div class="stat-info">
          <span class="stat-number">{{ totalPageCount }}</span>
          <span class="stat-label">Total Pages</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon stat-icon-green">
          <i class="fa fa-thumb-tack"></i>
        </div>
        <div class="stat-info">
          <span class="stat-number">{{ pinnedWikiPages.length }}</span>
          <span class="stat-label">Pinned</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon stat-icon-teal">
          <i class="fa fa-comment"></i>
        </div>
        <div class="stat-info">
          <span class="stat-number">{{ smsTemplateCount }}</span>
          <span class="stat-label">SMS Templates</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon stat-icon-amber">
          <i class="fa fa-tags"></i>
        </div>
        <div class="stat-info">
          <span class="stat-number">{{ siteTags.length }}</span>
          <span class="stat-label">Categories</span>
        </div>
      </div>
    </div>

    <WikiHomeSkeleton v-if="show" />

    <!-- Wiki Pages Panel Card -->
    <div class="wiki-panel" v-show="!show">
      <div class="panel-card">
        <div class="panel-header">
          <h5 class="section-title">
            <i class="fa fa-file-text-o section-icon"></i>
            Your Wiki Pages
          </h5>
        </div>

        <!-- Filter Row -->
        <div class="filter-row">
          <div class="filter-container">
            <div class="search-box">
              <i class="fa fa-search search-icon"></i>
              <input
                type="text"
                v-model="searchTerm"
                placeholder="Search wiki pages..."
                class="search-input"
                @input="onSearchInput"
                @keyup.enter="search"
              />
              <i
                v-if="searchTerm"
                class="fa fa-times-circle clear-icon"
                @click="clearSearch"
                title="Clear search"
              ></i>
              <i v-if="isSearching" class="fa fa-spinner fa-spin searching-icon"></i>
            </div>
          </div>

          <div class="tag-filters" v-if="siteTags && siteTags.length > 0">
            <router-link
              :to="{ path: `/app/wiki/`}"
              class="tag-filter-btn"
              :class="{ 'active': !$route.params.tags }"
            >
              All
            </router-link>
            <router-link
              v-for="(tag, index) in siteTags"
              :key="index"
              :to="{ path: `/app/wiki/${tag.Tag}`}"
              class="tag-filter-btn"
              :class="{ 'active': $route.params.tags === tag.Tag }"
            >
              {{ tag.Tag }}
            </router-link>
          </div>
        </div>

        <!-- Active filter indicator -->
        <div class="active-filters" v-if="$route.params.tags">
          <span class="filter-info">
            <i class="fa fa-filter"></i> Showing pages tagged:
            <span class="active-tag">{{ $route.params.tags }}</span>
          </span>
          <router-link :to="{ path: `/app/wiki/`}" class="clear-filter-btn">
            <i class="fa fa-times"></i> Clear Filter
          </router-link>
        </div>

        <!-- Empty state -->
        <div class="empty-state" v-if="wikiPages && wikiPages[0] && wikiPages[0].length === 0">
          <div class="empty-icon">
            <i class="fa fa-file-text-o"></i>
          </div>
          <h5 v-if="searchTerm">No results found</h5>
          <h5 v-else>No wiki pages yet</h5>
          <p v-if="searchTerm">No pages match "{{ searchTerm }}"</p>
          <p v-else>Create your first wiki page to start building your team's knowledge base.</p>
          <b-button v-if="!searchTerm" variant="success" class="create-btn mt-3" @click="gotoCreatePage()">
            <i class="fa fa-plus" aria-hidden="true"></i> New Wiki Page
          </b-button>
        </div>

        <!-- Wiki pages table -->
        <div v-if="wikiPages && wikiPages[0] && wikiPages[0].length > 0" class="wiki-table-container">
          <!-- Table Header -->
          <div class="wiki-table-header">
            <div class="sortable" @click="sortBy('Name')">
              Title
              <i :class="getSortIcon('Name')"></i>
            </div>
            <div>Tags</div>
            <div class="sortable" @click="sortBy('UpdatedAt')">
              Last Updated
              <i :class="getSortIcon('UpdatedAt')"></i>
            </div>
            <div class="text-right">Actions</div>
          </div>

          <!-- Pinned Wiki Pages Section -->
          <template v-if="pinnedWikiPages.length > 0">
            <div class="section-label pinned-section-label">
              <i class="fa fa-thumb-tack"></i> Pinned
            </div>
            <div
              class="wiki-item pinned-wiki-item"
              v-for="page in pinnedWikiPages"
              :key="'pinned-' + page.WikiPageID"
              @click="gotoViewPage(page.WikiPageID)"
            >
              <!-- Title -->
              <div class="text-title-wrap">
                <i class="fa fa-file-text-o wiki-icon" aria-hidden="true"></i>
                <div>
                  {{ page.Name }}
                  <span class="public-badge" v-if="page.IsPublic === 1">
                    <i class="fa fa-globe"></i> Public
                  </span>
                </div>
              </div>

              <!-- Tags -->
              <div class="tags-column">
                <div class="tags-container" v-if="page.Tags">
                  <span
                    v-for="(tag, tagIndex) in page.Tags.split(',').slice(0, 3)"
                    :key="tagIndex"
                    class="tag-badge"
                    @click.stop="goToTag(tag)"
                  >
                    {{ tag }}
                  </span>
                  <span class="more-tags" v-if="page.Tags.split(',').length > 3">
                    +{{ page.Tags.split(',').length - 3 }}
                  </span>
                </div>
                <span class="no-tags" v-else>—</span>
              </div>

              <!-- Last Updated -->
              <div class="date-column">{{ page.UpdatedAt | dateformat(dateFormatStr) }}</div>

              <!-- Actions -->
              <div class="actions-container" @click.stop>
                <!-- Pin/Unpin -->
                <i
                  v-if="pinningPageId === page.WikiPageID"
                  class="fa fa-spinner fa-spin action-icon pin-loading"
                ></i>
                <i
                  v-else
                  :id="`pin-wiki-${page.WikiPageID}`"
                  class="action-icon fa fa-thumb-tack pinned"
                  @click="handleTogglePin(page)"
                ></i>
                <b-tooltip v-if="pinningPageId !== page.WikiPageID" :target="`pin-wiki-${page.WikiPageID}`" placement="top" triggers="hover">
                  Unpin
                </b-tooltip>

                <router-link
                  :to="{ name: 'WikiEdit', params: { wikiPageID: `${page.WikiPageID}` } }"
                  :id="`edit-wiki-pinned-${page.WikiPageID}`"
                  class="action-icon"
                >
                  <i class="fa fa-pencil"></i>
                </router-link>
                <b-tooltip :target="`edit-wiki-pinned-${page.WikiPageID}`" placement="top" triggers="hover">
                  Edit Page
                </b-tooltip>

                <i
                  v-if="$can('delete','Page')"
                  :id="`delete-wiki-pinned-${page.WikiPageID}`"
                  class="fa fa-trash action-icon"
                  @click="showModal('delete', page.WikiPageID, pinnedWikiPages.indexOf(page), page.IsPublic === 1)"
                ></i>
                <b-tooltip v-if="$can('delete','Page')" :target="`delete-wiki-pinned-${page.WikiPageID}`" placement="top" triggers="hover">
                  Delete Page
                </b-tooltip>

                <template v-if="page.IsPublic === 1">
                  <i
                    v-if="copyingIndex === pinnedWikiPages.indexOf(page)"
                    class="fa fa-spinner fa-spin action-icon"
                  ></i>
                  <i
                    v-else
                    :id="`copy-link-pinned-${page.WikiPageID}`"
                    class="fa fa-link action-icon"
                    :class="{ 'copied': copiedPageId === page.WikiPageID }"
                    @click="copyPublicLink(page.WikiPageID, pinnedWikiPages.indexOf(page))"
                  ></i>
                  <b-tooltip v-if="copyingIndex !== pinnedWikiPages.indexOf(page)" :target="`copy-link-pinned-${page.WikiPageID}`" placement="top" triggers="hover">
                    {{ copiedPageId === page.WikiPageID ? 'Copied!' : 'Copy Public Link' }}
                  </b-tooltip>
                </template>
              </div>
            </div>
          </template>

          <!-- All Wiki Pages Section -->
          <template v-if="unpinnedWikiPages.length > 0">
            <div v-if="pinnedWikiPages.length > 0" class="section-label all-pages-label">
              All Pages
            </div>
            <div
              class="wiki-item"
              v-for="(page, index) in paginatedUnpinnedWikiPages"
              :key="'unpinned-' + page.WikiPageID"
              @click="gotoViewPage(page.WikiPageID)"
            >
              <!-- Title -->
              <div class="text-title-wrap">
                <i class="fa fa-file-text-o wiki-icon" aria-hidden="true"></i>
                <div>
                  {{ page.Name }}
                  <span class="public-badge" v-if="page.IsPublic === 1">
                    <i class="fa fa-globe"></i> Public
                  </span>
                </div>
              </div>

              <!-- Tags -->
              <div class="tags-column">
                <div class="tags-container" v-if="page.Tags">
                  <span
                    v-for="(tag, tagIndex) in page.Tags.split(',').slice(0, 3)"
                    :key="tagIndex"
                    class="tag-badge"
                    @click.stop="goToTag(tag)"
                  >
                    {{ tag }}
                  </span>
                  <span class="more-tags" v-if="page.Tags.split(',').length > 3">
                    +{{ page.Tags.split(',').length - 3 }}
                  </span>
                </div>
                <span class="no-tags" v-else>—</span>
              </div>

              <!-- Last Updated -->
              <div class="date-column">{{ page.UpdatedAt | dateformat(dateFormatStr) }}</div>

              <!-- Actions -->
              <div class="actions-container" @click.stop>
                <!-- Pin/Unpin -->
                <i
                  v-if="pinningPageId === page.WikiPageID"
                  class="fa fa-spinner fa-spin action-icon pin-loading"
                ></i>
                <i
                  v-else
                  :id="`pin-wiki-${page.WikiPageID}`"
                  class="action-icon fa fa-thumb-tack"
                  @click="handleTogglePin(page)"
                ></i>
                <b-tooltip v-if="pinningPageId !== page.WikiPageID" :target="`pin-wiki-${page.WikiPageID}`" placement="top" triggers="hover">
                  Pin to top
                </b-tooltip>

                <router-link
                  :to="{ name: 'WikiEdit', params: { wikiPageID: `${page.WikiPageID}` } }"
                  :id="`edit-wiki-${page.WikiPageID}`"
                  class="action-icon"
                >
                  <i class="fa fa-pencil"></i>
                </router-link>
                <b-tooltip :target="`edit-wiki-${page.WikiPageID}`" placement="top" triggers="hover">
                  Edit Page
                </b-tooltip>

                <i
                  v-if="$can('delete','Page')"
                  :id="`delete-wiki-${page.WikiPageID}`"
                  class="fa fa-trash action-icon"
                  @click="showModal('delete', page.WikiPageID, index, page.IsPublic === 1)"
                ></i>
                <b-tooltip v-if="$can('delete','Page')" :target="`delete-wiki-${page.WikiPageID}`" placement="top" triggers="hover">
                  Delete Page
                </b-tooltip>

                <template v-if="page.IsPublic === 1">
                  <i
                    v-if="copyingIndex === index"
                    class="fa fa-spinner fa-spin action-icon"
                  ></i>
                  <i
                    v-else
                    :id="`copy-link-${page.WikiPageID}`"
                    class="fa fa-link action-icon"
                    :class="{ 'copied': copiedPageId === page.WikiPageID }"
                    @click="copyPublicLink(page.WikiPageID, index)"
                  ></i>
                  <b-tooltip v-if="copyingIndex !== index" :target="`copy-link-${page.WikiPageID}`" placement="top" triggers="hover">
                    {{ copiedPageId === page.WikiPageID ? 'Copied!' : 'Copy Public Link' }}
                  </b-tooltip>
                </template>

                <i class="fa fa-spinner fa-spin action-icon" v-if="loadingStatus[index]"></i>
              </div>
            </div>
          </template>
        </div>

        <!-- Pagination -->
        <div class="pagination-container" v-if="totalPages > 1 || totalItems > 10">
          <div class="pagination-info">
            Showing {{ startItem }}-{{ endItem }} of {{ totalItems }}
          </div>
          <div class="pagination-controls">
            <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage = 1">
              <i class="fa fa-angle-double-left"></i>
            </button>
            <button class="pagination-btn" :disabled="currentPage === 1" @click="previousPage">
              <i class="fa fa-angle-left"></i>
            </button>
            <span class="pagination-page">Page {{ currentPage }} of {{ totalPages }}</span>
            <button class="pagination-btn" :disabled="currentPage === totalPages" @click="nextPage">
              <i class="fa fa-angle-right"></i>
            </button>
            <button class="pagination-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
              <i class="fa fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Modal -->
    <b-modal ref="delete" id="delete" :title="wikiReferences.length > 0 ? '⚠️ Warning: Page In Use' : 'Delete Wiki Page'" body-class="bg-white" centered>
      <div v-if="checkingReferences" class="text-center py-3">
        <i class="fa fa-spinner fa-spin"></i> Checking if any templates or pages link to this wiki...
      </div>
      <div v-else-if="wikiReferences.length > 0">
        <div class="delete-warning-banner">
          <i class="fa fa-exclamation-triangle"></i>
          <strong>This page is referenced by the following templates/pages:</strong>
        </div>
        <ul class="delete-reference-list">
          <li v-for="ref in wikiReferences" :key="ref.id">
            <span class="ref-type-badge">{{ ref.type }}</span>
            <a :href="'/app/wiki-edit/' + ref.id" target="_blank" class="ref-link">{{ ref.name }} <i class="fa fa-external-link"></i></a>
          </li>
        </ul>
        <p class="mt-2 mb-2" style="font-size: 0.9em; color: #666;">Deleting this page will cause broken links in the items listed above.</p>
        <div v-if="selectedWikiIsPublic" class="shared-links-warning">
          <i class="fa fa-chain-broken"></i>
          <div>
            <strong>Shared link will stop working</strong>
            <p>This page has a public link. If you've shared that link with anyone, they will no longer be able to open it. If you haven't shared it, this is safe to delete.</p>
          </div>
        </div>
        <label class="delete-acknowledge-label">
          <input type="checkbox" v-model="deleteAcknowledged" />
          I understand these templates/pages will have broken links
        </label>
      </div>
      <div v-else>
        <div class="delete-modal-content">
          <i class="fa fa-exclamation-triangle warning-icon"></i>
          <p>Are you sure you want to delete this wiki page?</p>
          <p class="warning-text">This action cannot be undone.</p>
        </div>
        <div v-if="selectedWikiIsPublic" class="shared-links-warning">
          <i class="fa fa-chain-broken"></i>
          <div>
            <strong>Shared link will stop working</strong>
            <p>This page has a public link. If you've shared that link with anyone, they will no longer be able to open it. If you haven't shared it, this is safe to delete.</p>
          </div>
        </div>
      </div>
      <div slot="modal-footer" class="modal-footer-btns">
        <b-button variant="outline-secondary" @click="hideModal('delete')">Cancel</b-button>
        <b-button v-if="wikiReferences.length > 0" variant="danger" :disabled="!deleteAcknowledged || checkingReferences" @click="del">Delete Anyway</b-button>
        <b-button v-else variant="danger" :disabled="checkingReferences" @click="del">Delete</b-button>
      </div>
    </b-modal>

    <!-- Edit Access Modal -->
    <b-modal ref="edit" id="edit" title="Edit Access" body-class="bg-white" centered>
      <h5 class="mb-4">
        Page:
        <strong class="ml-2">Page Title</strong>
      </h5>
      <b-form-group class="abc-checkbox abc-checkbox-success">
        <input type="checkbox" id="checkbox-success29" v-model="allCanView" disabled/>
        <label for="checkbox-success29">
          All team members can view this page
        </label>
      </b-form-group>

      <b-form-group class="abc-checkbox abc-checkbox-success">
        <input type="checkbox" id="checkbox-success30" v-model="allCanEdit"/>
        <label for="checkbox-success30">
          All team members can edit this page
        </label>
      </b-form-group>

      <div id="individual-access" v-if="!allCanEdit">
        <p class="add-edit-text">Edit access:</p>
        <p>The following team members can change/edit the page</p>

        <multiselect
            v-if="this.employees"
            v-model="editors"
            :options="this.employees"
            :multiple="true"
            :taggable="false"
            :show-labels="false"
            label="Name"
            track-by="Name"
            aria-placeholder
          >
            <template slot="singleLabel" slot-scope="props">
              <span class="custom__tag">
                <img
                  class="option__image"
                  src="../../assets/icons-user-40.png"
                  alt="Team member image"
                />
                <span>{{ props.option.Name }}</span>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <img
                class="option__image"
                src="../../assets/icons-user-40.png"
                alt="Team member image"
              />
              <div class="option__desc">
                <span class="option__title">{{ props.option.Name }}</span>
              </div>
            </template>
        </multiselect>
      </div>

      <div slot="modal-footer" class="modal-footer-btns">
        <b-button variant="outline-secondary" @click="hideModal('edit')">Cancel</b-button>
        <b-button variant="success" @click="show=false">Save</b-button>
      </div>
    </b-modal>

    <!-- Wiki Import Modal -->
    <WikiImportModal ref="importModal" @imported="init" />
  </div>
</template>

<script>
/* eslint-disable */

import Multiselect from "vue-multiselect";
import { mapState, mapActions, mapMutations } from 'vuex';
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import WikiImportModal from "@/pages/WikiImport/WikiImportModal";
import CommonQueries from '../../sql/queries';
import { stringify } from 'querystring';
import mixin from '../../mixin.js';
import WikiHomeSkeleton from './WikiHomeSkeleton.vue';

export default {
  name: "WikiHome",
  components: { Multiselect, ErrorDisplay, WikiHomeSkeleton, WikiImportModal },
  mixins: [mixin],

  created() {
    if(this.allSettings)
        this.init();
  },

  data() {
    return {
      show: false,
      wikiPages: [],
      siteTags: [],
      editors: [],
      selectedWikiPageID: 0,
      selectedWikiIsPublic: false,
      wikiReferences: [],
      checkingReferences: false,
      deleteAcknowledged: false,
      allCanView: false,
      allCanEdit: false,
      simpleDropdownVariant: "Select",
      isPickerActive: false,
      loadingStatus: [].repeat(false, 50),
      selectedIndex: null,
      copyingIndex: null,
      copiedPageId: null,
      pinningPageId: null,
      sortField: 'UpdatedAt',
      sortDirection: 'desc',
      searchTerm: '',
      searchDebounceTimer: null,
      isSearching: false,
      currentPage: 1,
      itemsPerPage: 10,
      itemsPerPageOptions: [10, 25, 50, 100]
    };
  },

  computed: {
    ...mapState({
        loading: state => state.alert.loading,
        loaded: state => state.alert.loaded,
        errored: state => state.alert.errored,
        alertMessage: state => state.alert.message,
        dismissSuccessCountDown : state => state.alert.dismissSuccessCountDown,
        siteID: state => state.customer.siteID,
        userID: state => state.customer.userID,
        sites: state => state.customer.sites,
        employees: state => state.customer.employees,
    }),

    totalPageCount() {
      if (!this.wikiPages || !this.wikiPages[0]) return 0;
      return this.wikiPages[0].length;
    },

    smsTemplateCount() {
      if (!this.wikiPages || !this.wikiPages[0]) return 0;
      return this.wikiPages[0].filter(p =>
        p.Tags && p.Tags.toLowerCase().includes('sms-template')
      ).length;
    },

    sortedWikiPages() {
      if (!this.wikiPages || !this.wikiPages[0]) return [];

      const pages = [...this.wikiPages[0]];
      return pages.sort((a, b) => {
        // Pinned pages always come first
        const aPinned = a.IsPinned ? 1 : 0;
        const bPinned = b.IsPinned ? 1 : 0;
        if (aPinned !== bPinned) {
          return bPinned - aPinned;
        }

        let aVal = a[this.sortField];
        let bVal = b[this.sortField];

        // Handle string comparison for Name
        if (this.sortField === 'Name') {
          aVal = (aVal || '').toLowerCase();
          bVal = (bVal || '').toLowerCase();
        }

        // Handle date comparison
        if (this.sortField === 'CreatedAt' || this.sortField === 'UpdatedAt') {
          aVal = new Date(aVal || 0);
          bVal = new Date(bVal || 0);
        }

        if (this.sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    },

    pinnedWikiPages() {
      return this.sortedWikiPages.filter(page => page.IsPinned);
    },

    unpinnedWikiPages() {
      return this.sortedWikiPages.filter(page => !page.IsPinned);
    },

    // Pagination computed properties (only for unpinned pages)
    totalPages() {
      return Math.ceil(this.unpinnedWikiPages.length / this.itemsPerPage);
    },

    paginatedUnpinnedWikiPages() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.unpinnedWikiPages.slice(start, end);
    },

    totalItems() {
      return this.unpinnedWikiPages.length;
    },

    startItem() {
      if (this.totalItems === 0) return 0;
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    endItem() {
      const end = this.currentPage * this.itemsPerPage;
      return end > this.totalItems ? this.totalItems : end;
    },

    // Generate page numbers for pagination
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 7) {
        // Show all pages if 7 or fewer
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);

        if (current > 3) {
          pages.push('...');
        }

        // Show pages around current
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);

        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        if (current < total - 2) {
          pages.push('...');
        }

        // Always show last page
        pages.push(total);
      }

      return pages;
    }
  },

  methods: {
    ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
    ...mapActions('wiki', ['getWikiPages', 'getSiteTags', 'deleteWiki', 'checkWikiReferences', 'getWikiUrl', 'togglePinWiki']),
    ...mapActions('customer', ['getEmployees']),

    async init() {
      this.resetError();
      this.show = true;
      if(this.$route.params.tags) {
        let tags = this.$route.params.tags;
        this.wikiPages = await this.getWikiPages({siteID: this.siteID, tags: tags});
      } else {
        this.wikiPages = await this.getWikiPages({siteID: this.siteID, searchTerm: this.searchTerm});
      }
      this.siteTags = await this.getSiteTags({siteID: this.siteID});
      this.show = false;
    },

    search() {
      this.show = true;
      this.init();
    },

    // Debounced search - triggers after user stops typing
    onSearchInput() {
      // Clear any existing timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      // Set a new timer to search after 300ms of no typing
      this.searchDebounceTimer = setTimeout(() => {
        this.performSearch();
      }, 300);
    },

    async performSearch() {
      this.isSearching = true;
      this.currentPage = 1; // Reset to first page on new search
      try {
        this.wikiPages = await this.getWikiPages({siteID: this.siteID, searchTerm: this.searchTerm});
      } finally {
        this.isSearching = false;
      }
    },

    clearSearch() {
      this.searchTerm = '';
      this.performSearch();
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
      // Reset to first page when sorting changes
      this.currentPage = 1;
    },

    getSortIcon(column) {
      if (this.sortField !== column) {
        return 'fa fa-sort';
      }
      return this.sortDirection === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc';
    },

    // Pagination methods
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    changeItemsPerPage(count) {
      this.itemsPerPage = count;
      this.currentPage = 1; // Reset to first page
    },

    async showModal(name, wikiPageID, index, isPublic = false) {
      this.selectedWikiPageID = wikiPageID;
      this.selectedIndex = index;

      if (name === 'delete') {
        this.wikiReferences = [];
        this.deleteAcknowledged = false;
        this.selectedWikiIsPublic = isPublic;
        this.checkingReferences = true;
        this.$refs[name].show();
        try {
          const result = await this.checkWikiReferences({ WikiPageID: wikiPageID });
          this.wikiReferences = result.references || [];
        } catch (e) {
          console.log('Error checking references:', e);
          this.wikiReferences = [];
        }
        this.checkingReferences = false;
        return;
      }

      this.$refs[name].show();
    },

    hideModal(ref) {
      this.$refs[ref].hide()
    },

    openImportModal() {
      this.$refs.importModal.show();
    },

    gotoCreatePage() {
      this.$router.push({
        name: 'WikiCreate',
        params: {
          pages : this.wikiPages
        }
      });
    },

    gotoViewPage(wikiPageID) {
        this.$router.push({
        name: 'WikiView',
        params: {
          wikiPageID : wikiPageID
        }
      });
    },

    goToTag(tag) {
      this.$router.push({ path: `/app/wiki/${tag}` });
    },

    async del() {
      try {
        this.show = !this.show;
        this.hideModal('delete');
        await this.deleteWiki({WikiPageID: this.selectedWikiPageID});
        this.show = !this.show;
        this.$toast.success("Wiki deleted successfully!");
        this.$router.go(0);
      } catch(e) {
        this.show = !this.show;
        console.log('Error' + e);
        this.$toast.error("Error deleting wiki page. Please try again later");
      }
    },

    async copyPublicLink(wikiPageID, index) {
      try {
        this.copyingIndex = index;
        this.copiedPageId = null;

        const payload = {
          siteID: this.siteID,
          wikiPageID: wikiPageID
        };

        const response = await this.getWikiUrl(payload);

        if (response && response.data) {
          await navigator.clipboard.writeText(response.data);
          this.copiedPageId = wikiPageID;
          this.$toast.success("Public link copied to clipboard!");

          setTimeout(() => {
            this.copiedPageId = null;
          }, 3000);
        }
      } catch (error) {
        console.error('Error copying public wiki URL:', error);
        this.$toast.error('Failed to copy public link. Please try again.');
      } finally {
        this.copyingIndex = null;
      }
    },

    async handleTogglePin(page) {
      if (this.pinningPageId) return;
      const newPinState = !page.IsPinned;
      this.pinningPageId = page.WikiPageID;
      try {
        await this.togglePinWiki({
          wikiPageID: page.WikiPageID,
          siteID: this.siteID,
          isPinned: newPinState
        });
        page.IsPinned = newPinState ? 1 : 0;
      } catch (err) {
        console.error('Error toggling pin:', err);
        this.$toast.error('Failed to toggle pin. Please try again.');
      } finally {
        this.pinningPageId = null;
      }
    }
  },

  watch: {
    allSettings(newValue, oldValue) {
      if(!oldValue) {
        this.init();
      }
    }
  },

  beforeDestroy() {
    // Clean up the debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  }

};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style src="./WikiHome.scss" lang="scss" scoped></style>
